import * as luxon from 'luxon';
import React, { useEffect, useState } from 'react';
import { Alert } from 'react-bootstrap';
import Nav from 'react-bootstrap/Nav';
import { useApi } from '../classes/appApi';
import { useOffline } from '../components/App/AppOfflineContext';
import MainLayout, { useMainLayoutContext } from '../components/MainLayout';
import { AbsenceModel, HolidayEntitlementAllowanceModel, HolidayEntitlementModel } from '../defs/defs';
import eqxEndpoint from '../shared/classes/eqxEndpoint';
import EqxBadge from '../shared/components/EqxBadge/EqxBadge';
import EqxCalendar from '../shared/components/EqxCalendar';
import EqxCard from '../shared/components/EqxCard/EqxCard';
import EqxError from '../shared/components/EqxError';
import EqxIcon from '../shared/components/EqxIcon';
import EqxListGroup, { EqxListGroupItem } from '../shared/components/EqxListGroup/EqxListGroup';
import EqxLoading from '../shared/components/EqxLoading';
import EqxPatch from '../shared/components/EqxPatch';
import EqxQuotaBar from '../shared/components/EqxQuotaBar/EqxQuotaBar';
import EqxRequestHandler from '../shared/components/EqxRequestHandler/EqxRequestHandler';
import EqxTile, { EqxTileBlank } from '../shared/components/EqxTile/EqxTile';
import EqxTileGroup from '../shared/components/EqxTile/EqxTileGroup';
import useApiEndpoint from '../shared/hooks/ApiEndpoint';
import { formatDate } from '../shared/utils/formatDate';

// AbsenceListItem component extracted from main render

interface AbsenceListItemProps {
    absence: AbsenceModel;
    entitlement: HolidayEntitlementModel | null;
}

const AbsenceListItem: React.FC<AbsenceListItemProps> = ({ absence: abs, entitlement: ent }) => {
    const useHours = ent?.entitlementHours && ent.entitlementHours > 0;
    const [open, setOpen] = useState(false);
    const hasNotes = abs.notes && abs.notes.trim().length > 0;
    const handleToggle = () => {
        if (hasNotes) {
            setOpen(o => !o);
        }
    };
    const calendarDays = abs.finishDate ? Math.max(0, luxon.DateTime.fromISO(abs.finishDate).diff(luxon.DateTime.fromISO(abs.startDate), 'days').days + 1 - (abs.startDateIsHalfDay ? 0.5 : 0) - (abs.finishDateIsHalfDay ? 0.5 : 0)) : 0;
    return (
        <>
            <EqxListGroupItem key={abs.id} onClick={handleToggle} style={{ cursor: hasNotes ? 'pointer' : 'default' }}>
                <div className="d-flex align-items-stretch">
                    <div>
                        <h6><EqxPatch colour={abs.absenceType?.colour} /></h6>
                    </div>
                    <div className="flex-grow-1">
                        <h6>{abs.absenceType?.name}</h6>
                        <div className="subtitle text-muted">
                            {abs.startDate && abs.finishDate && abs.startDate == abs.finishDate ? (
                                <>
                                    {formatDate(abs.startDate)}
                                    {abs.startDateIsHalfDay ? " (pm)" : ""}
                                    {abs.finishDateIsHalfDay ? " (am)" : ""}
                                </>
                            ) : (
                                <>
                                    {formatDate(abs.startDate)}{abs.startDateIsHalfDay ? " (pm)" : ""}
                                    {" - "}
                                    {abs.finishDate ? formatDate(abs.finishDate) + (abs.finishDateIsHalfDay ? " (am)" : "") : "Open Ended"}
                                </>
                            )}
                        </div>
                    </div>
                    <div className="text-end">
                        {abs.approved === null ? (
                            <EqxBadge variant="warning" icon="fas fa-clock" text="Not yet Approved" />
                        ) : abs.approved ? (
                            <EqxBadge variant="success" icon="fas fa-check" text="Approved" />
                        ) : (
                            <EqxBadge variant="danger" icon="fas fa-times" text="Rejected" />
                        )}
                        <br />
                        {abs.workingDays === undefined && abs.workingHours === undefined ? (
                            abs.finishDate && <EqxBadge variant="secondary" icon="fas fa-calendar" text={(calendarDays) + " calendar days"} />
                        ) : (
                            useHours ?
                                (abs.workingHours !== null && abs.workingHours !== undefined) ?
                                    <EqxBadge variant="secondary" text={abs.workingHours + " working hours"} /> : null
                                : (abs.workingDays !== null && abs.workingDays !== undefined) ?
                                    <EqxBadge variant="secondary" text={abs.workingDays + " working days"} /> : null
                        )}
                    </div>
                </div>
                {hasNotes && open && (
                    <EqxCard className="bg-light border-0 mb-0">
                        <strong>Notes:</strong><br />{abs.notes}
                    </EqxCard>
                )}
                {hasNotes && !open && (
                    <div className="text-center"><EqxIcon fa="fas fa-chevron-down" /></div>
                )}
                {!hasNotes && (
                    <div className="text-center py-2"></div>
                )}
            </EqxListGroupItem>
        </>
    );
};

// --- Quota Bar Component ---
interface HolidayEntitlementQuotaBarProps {
    selected: HolidayEntitlementModel | null;
    absences: AbsenceModel[]; //I dont think we need absences here anymore?
    allowance?: HolidayEntitlementAllowanceModel | null;
}
const HolidayEntitlementQuotaBar: React.FC<HolidayEntitlementQuotaBarProps> = ({ selected, absences, allowance }) => {
    if (!selected) return null;
    // Determine units
    const useHours = selected.entitlementHours && selected.entitlementHours > 0;
    const quota = useHours ? selected.entitlementHours : selected.entitlement;
    const unitLabel = useHours ? 'Hours' : 'Days';

    let taken = 0, upcoming = 0, requested = 0;

    if (allowance) {
        taken = allowance.taken ?? 0;
        upcoming = allowance.upcoming ?? 0;
        requested = allowance.requested ?? 0;
    }

    // Build items array
    const items = [];
    if (taken > 0) items.push({ id: 'taken', label: 'Taken', amount: taken, colour: 'success' });
    if (upcoming > 0) items.push({ id: 'upcoming', label: 'Upcoming', amount: upcoming, colour: 'success', ghosted: true });
    if (requested > 0) items.push({ id: 'requested', label: 'Requested', amount: requested, colour: (amount: number) => (amount > quota ? 'danger' : 'warning') });

    // EqxQuotaBar options
    return (
        <EqxQuotaBar
            quota={quota}
            items={items}
            divideOverThresholdItem={true}
            useItemsForColours={true}
            format={amount => `${amount} ${unitLabel}`}
            labels={{
                used: { label: 'Used', show: true },
                quota: { label: 'Holiday Allowance', show: true },
                unused: { label: 'Available', show: true },
            }}
        />
    );
};
// --- End Quota Bar Component ---

// AbsenceListCard component
interface AbsenceListCardProps {
    selected: HolidayEntitlementModel | null;
    isChild?: boolean;
    calendarValue: Date | undefined;
    setCalendarValue: (date: Date) => void;
    calendarMin?: Date;
    calendarMax?: Date;
}
const AbsenceListCard: React.FC<AbsenceListCardProps> = ({ selected, isChild, calendarValue, setCalendarValue, calendarMin, calendarMax }) => {
    const api = useApi();
    const [absences, setAbsences] = useState<AbsenceModel[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [allowance, setAllowance] = useState<HolidayEntitlementAllowanceModel | null>(null);
    const [allowanceLoading, setAllowanceLoading] = useState(false);
    const [allowanceError, setAllowanceError] = useState<string | null>(null);

    let endpoint = api.endpoints.unassignedAbsences();
    let holidayEntitlementAllowanceEndpoint: eqxEndpoint<HolidayEntitlementAllowanceModel>;

    if (selected) {
        endpoint = api.endpoints.holidayEntitlementAbsences(selected.id);
        holidayEntitlementAllowanceEndpoint = api.endpoints.holidayEntitlementAllowance(selected.id);
    }

    React.useEffect(() => {
        setLoading(true);
        endpoint.execute(api).then(absences => {
            setAbsences(absences);
            setLoading(false);
        }).catch(error => {
            setError(error);
            setLoading(false);
        });
    }, [selected]);

    React.useEffect(() => {
        if (selected && holidayEntitlementAllowanceEndpoint) {
            setAllowanceLoading(true);
            holidayEntitlementAllowanceEndpoint.execute(api)
                .then(data => {
                    setAllowance(data);
                    setAllowanceLoading(false);
                })
                .catch(error => {
                    setAllowanceError(error?.message || 'Failed to load allowance');
                    setAllowanceLoading(false);
                });
        } else {
            setAllowance(null);
        }
    }, [selected]);

    return (
        <EqxRequestHandler
            state={{ loading, error }}
            isEmpty={absences.length === 0}
            empty={
                <EqxCard isSection={true} className="p-5">
                    <div className="text-center text-muted">
                        <EqxIcon fa="fas fa-calendar-times" />
                        <br />
                        No absences found
                    </div>
                </EqxCard >
            }
            content={() => (
                <>
                    {selected ? (
                        <EqxCard key="quota" isSection={true} title={"Holiday Allowance " + selected.displayName} subtitle={formatDate(selected.periodStart) + " - " + formatDate(selected.periodFinish)} collapsible={false}>
                            {allowanceLoading ? <EqxLoading /> : allowanceError ? <EqxError error={allowanceError} /> : <HolidayEntitlementQuotaBar selected={selected} absences={absences} allowance={allowance} />}
                        </EqxCard>
                    ) :
                        <Alert variant="warning">The following items do not fall under any holiday entitlement</Alert>
                    }
                    <EqxCard key="list" noPadding={true} isSection={true}>
                        <EqxListGroup isChild={isChild}>
                            {absences.map(abs => (
                                <AbsenceListItem key={abs.id} absence={abs} entitlement={selected} />
                            ))}
                        </EqxListGroup>
                    </EqxCard>
                    <EqxCard key="calendar" isSection={true} noPadding={true}>
                        <div className="d-flex justify-content-center">
                            <EqxCalendar
                                value={calendarValue}
                                min={calendarMin}
                                max={calendarMax}
                                onChange={setCalendarValue}
                                highlightRanges={absences.map(abs => ({
                                    start: new Date(abs.startDate),
                                    end: new Date(abs.finishDate),
                                    isHalfDayStart: abs.startDateIsHalfDay,
                                    isHalfDayEnd: abs.finishDateIsHalfDay,
                                    colour: abs.absenceType?.colour
                                }))}
                            />
                        </div>
                    </EqxCard>
                </>
            )}
        />
    );
};

// Main content component extracted from PageAbsences
const AbsencesContent: React.FC = () => {
    const api = useApi();
    const endpoint = api.endpoints.holidayEntitlements();
    const { response: entitlements, loading, error, reset } = useApiEndpoint<HolidayEntitlementModel[]>(api, endpoint);
    const [selected, setSelected] = useState<HolidayEntitlementModel | null>(null);
    // Calendar state
    const [calendarValue, setCalendarValue] = useState<Date | undefined>(undefined);
    const { isOffline } = useOffline()
    useEffect(() => {
        if (entitlements) {
            let current = entitlements.find(e => e.isCurrent);
            if (current) setSelected(current);
            else if (entitlements.length > 0) setSelected(entitlements[0]);
            else setSelected(null);
        }
    }, [entitlements]);

    // Set calendar to first of entitlement period or today
    useEffect(() => {
        //if selected and current date is not within the selected items period (start and finish), set to start of period
        if (selected && calendarValue) {
            const start = luxon.DateTime.fromISO(selected.periodStart);
            const end = luxon.DateTime.fromISO(selected.periodFinish);
            const today = luxon.DateTime.now();
            const startAsDate = start.toISODate();
            debugger;
            if ((today < start || today > end) && startAsDate) {
                setCalendarValue(new Date(startAsDate));
            } else {
                setCalendarValue(new Date());
            }
        } else {
            setCalendarValue(new Date());
        }
    }, [selected]);

    // Check allowed - moved after all hooks to follow Rules of Hooks
    const mainContext = useMainLayoutContext()
    if (!mainContext.permissions?.canBookAbsence) {
        return <EqxError error="You do not have permission to view absences." />
    }
    if (!mainContext.user?.staffID) {
        if (!mainContext.user) {
            return <EqxLoading />
        } else {
            return <EqxError error="Only staff can view absences" />;
        }
    }

    const handleSelect = (ent: HolidayEntitlementModel | null) => {
        setSelected(ent);
    };

    // Calendar min/max
    const calendarMin = selected ? new Date(selected.periodStart) : undefined;
    const calendarMax = selected ? new Date(selected.periodFinish) : undefined;

    return (
        <>
            <EqxRequestHandler
                state={{ loading, error }}
                content={() => (
                    <>
                        <Nav variant="pills" className="mb-3 justify-content-end">
                            {entitlements && entitlements.map((ent: HolidayEntitlementModel) => (
                                <Nav.Item key={ent.id}>
                                    <Nav.Link
                                        active={selected !== null && selected.id === ent.id}
                                        onClick={() => handleSelect(ent)}
                                    >
                                        {ent.displayName}
                                    </Nav.Link>
                                </Nav.Item>
                            ))}
                            <Nav.Item key="unassigned">
                                <Nav.Link
                                    active={selected === null}
                                    onClick={() => handleSelect(null)}
                                >
                                    Unassigned
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <AbsenceListCard
                            selected={selected}
                            isChild={true}
                            calendarValue={calendarValue}
                            setCalendarValue={setCalendarValue}
                            calendarMin={calendarMin}
                            calendarMax={calendarMax}
                        />
                    </>
                )}
            />
            <EqxTileGroup size="xs">
                <EqxTile
                    className='col-6'
                    title="Request Absence"
                    icon="fas fa-calendar-plus"
                    color="success"
                    link="/absences/create"
                    disabled={isOffline}
                />
                <EqxTileBlank className='col-6' />
            </EqxTileGroup>
        </>
    );
};

const PageAbsences: React.FC = () => {
    return (
        <MainLayout pageTitle="Absences">
            <AbsencesContent />
        </MainLayout>
    );
};

export default PageAbsences;
export { AbsenceListItem, AbsencesContent };

